// Copyright (c) Ping. All rights reserved.
//
// Local, at-rest-encrypted store for the user's 12-word recovery
// mnemonic so it can be REVEALED later (Settings → Account recovery →
// "Reveal recovery phrase") after the post-signup background backup ran
// without ever showing the phrase to the user up front.
//
// The mnemonic is wrapped with the SAME AES-GCM wrapper key that
// protects the auth tokens (see `core/persistence/tokens.ts`) and stored
// under `__recovery.mnemonic` in `settings_kv`. A separate boolean flag
// `__recovery.mnemonicSavedConfirmed` records that the user has written
// the phrase down; on confirmation the stored mnemonic is PURGED so the
// secret no longer lingers on disk.

import { db } from '@core/persistence/db';
import {
  unwrapSecretFromStorage,
  wrapSecretForStorage,
} from '@core/persistence/tokens';

/** settings_kv key holding the AES-GCM-wrapped mnemonic. */
export const RECOVERY_MNEMONIC_KV_KEY = '__recovery.mnemonic';
/** settings_kv key holding the "user confirmed they saved it" flag. */
export const RECOVERY_MNEMONIC_CONFIRMED_KV_KEY =
  '__recovery.mnemonicSavedConfirmed';

interface WrappedMnemonicRecord {
  iv: number[];
  ciphertext: number[];
}

/**
 * Persist the recovery mnemonic locally, encrypted at rest with the
 * shared AES-GCM wrapper key. Resets the "saved confirmed" flag so a
 * freshly-stored phrase is treated as un-acknowledged until the user
 * reveals + confirms it.
 */
export async function storeRecoveryMnemonic(mnemonic: string): Promise<void> {
  await db.open();
  const wrapped = await wrapSecretForStorage(mnemonic);
  await db.settings_kv.put({
    key: RECOVERY_MNEMONIC_KV_KEY,
    value: wrapped,
    updatedAt: Date.now(),
  });
  await db.settings_kv.put({
    key: RECOVERY_MNEMONIC_CONFIRMED_KV_KEY,
    value: false,
    updatedAt: Date.now(),
  });
}

/**
 * Read + decrypt the stored mnemonic, or `null` if none is stored (or it
 * can't be decrypted — e.g. the wrapper key is gone). Never throws.
 */
export async function retrieveRecoveryMnemonic(): Promise<string | null> {
  await db.open();
  const row = await db.settings_kv.get(RECOVERY_MNEMONIC_KV_KEY);
  const value = row?.value as WrappedMnemonicRecord | undefined;
  if (
    !value ||
    !Array.isArray(value.iv) ||
    !Array.isArray(value.ciphertext)
  ) {
    return null;
  }
  return unwrapSecretFromStorage(value);
}

/** True once the user has confirmed they wrote the phrase down. */
export async function readRecoveryMnemonicConfirmed(): Promise<boolean> {
  await db.open();
  const row = await db.settings_kv.get(RECOVERY_MNEMONIC_CONFIRMED_KV_KEY);
  return row?.value === true;
}

/**
 * Mark the mnemonic as saved-by-the-user and PURGE the stored phrase so
 * the plaintext-recoverable secret no longer sits on disk. Idempotent.
 */
export async function confirmRecoveryMnemonicSaved(): Promise<void> {
  await db.open();
  await db.settings_kv.put({
    key: RECOVERY_MNEMONIC_CONFIRMED_KV_KEY,
    value: true,
    updatedAt: Date.now(),
  });
  await db.settings_kv.delete(RECOVERY_MNEMONIC_KV_KEY);
}
